import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { apiFetch } from '../../utils/api'
import { centsToEur } from '../paie/payroll-engine'
import styles from './DashboardPage.module.css'

interface DashboardData {
  employeeCount: number
  lastPayrollPeriod: string | null
  lastPayrollTotal: number
  pendingLeaveRequests: number
}

export function DashboardPage() {
  const { t } = useTranslation()
  const [data, setData] = useState<DashboardData>({
    employeeCount: 0,
    lastPayrollPeriod: null,
    lastPayrollTotal: 0,
    pendingLeaveRequests: 0,
  })

  useEffect(() => {
    // Load real data
    Promise.all([
      apiFetch<{ employees: Array<{ id: string }> }>('/employees?active=true'),
      apiFetch<{ payslips: Array<Record<string, unknown>> }>('/payslips'),
      apiFetch<{ leaveRequests: Array<{ status: string }> }>('/absences/leave-requests?status=pending'),
    ]).then(([empRes, payRes, leaveRes]) => {
      const employees = empRes.data?.employees ?? []
      const payslips = payRes.data?.payslips ?? []
      const pending = leaveRes.data?.leaveRequests ?? []

      let lastPeriod: string | null = null
      let lastTotal = 0
      if (payslips.length > 0) {
        const latest = payslips[0]!
        lastPeriod = `${String(latest['period_month']).padStart(2, '0')}/${latest['period_year']}`
        lastTotal = payslips.reduce((s, p) => s + ((p['net_salary_cents'] as number) ?? 0), 0)
      }

      setData({
        employeeCount: employees.length,
        lastPayrollPeriod: lastPeriod,
        lastPayrollTotal: lastTotal,
        pendingLeaveRequests: pending.length,
      })
    })
  }, [])

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>{t('dashboard.welcome')}</h1>
      <p className={styles.subtitle}>{t('dashboard.subtitle')}</p>

      <div className={styles.grid}>
        <Link to="/employees" className={styles.card}>
          <span className={styles.cardValue}>{data.employeeCount}</span>
          <h3 className={styles.cardTitle}>{t('nav.employees')}</h3>
          <p className={styles.cardDesc}>{t('dashboard.active_employees')}</p>
        </Link>

        <Link to="/payroll" className={styles.card}>
          <span className={styles.cardValue}>
            {data.lastPayrollPeriod ? centsToEur(data.lastPayrollTotal) : '—'}
          </span>
          <h3 className={styles.cardTitle}>{t('nav.payroll')}</h3>
          <p className={styles.cardDesc}>
            {data.lastPayrollPeriod ? `${t('dashboard.last_payroll')} ${data.lastPayrollPeriod}` : t('dashboard.no_payroll')}
          </p>
        </Link>

        <Link to="/absences" className={styles.card}>
          <span className={styles.cardValue}>{data.pendingLeaveRequests}</span>
          <h3 className={styles.cardTitle}>{t('nav.absences')}</h3>
          <p className={styles.cardDesc}>{t('dashboard.pending_requests')}</p>
        </Link>
      </div>

      {/* Quick actions */}
      <div className={styles.quickActions}>
        <Link to="/payroll" className={styles.quickBtn}>{t('run_payroll.title')}</Link>
        <Link to="/employees/new" className={styles.quickBtn}>{t('employees.add')}</Link>
        <Link to="/payroll/simulator" className={styles.quickBtn}>{t('simulator.title')}</Link>
      </div>
    </div>
  )
}
